import { supabase } from '../lib/supabase';

// Helper function for snake_case to camelCase conversion
const toCamelCase = (obj) => {
  if (!obj) return obj;
  
  const converted = {};
  Object.keys(obj)?.forEach((key) => {
    const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
    converted[camelKey] = obj?.[key];
  });
  return converted;
};

// Helper function for camelCase to snake_case conversion
const toSnakeCase = (obj) => {
  if (!obj) return obj;
  
  const converted = {};
  Object.keys(obj)?.forEach((key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    converted[snakeKey] = obj?.[key];
  });
  return converted;
};

// Helper to format posted date
const formatPostedTime = (dateString) => {
  const now = new Date();
  const posted = new Date(dateString);
  const diffMs = now - posted;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
  return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? 's' : ''} ago`;
};

export const jobService = {
  // Get all jobs for current employer
  async getEmployerJobs() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('jobs')?.select('*')?.eq('employer_id', user?.id)?.order('posted_date', { ascending: false });

    if (error) throw error;
    
    // Convert snake_case to camelCase
    return data?.map((job) => toCamelCase(job)) || [];
  },

  // Get single job by ID
  async getJobById(jobId) {
    const { data, error } = await supabase
      ?.from('jobs')
      ?.select(`
        *,
        employer:user_profiles!employer_id(company_name, location)
      `)
      ?.eq('id', jobId)
      ?.single();

    if (error) throw error;
    
    const job = data;
    const camelJob = toCamelCase(job);
    
    // Handle skills - convert array of objects or strings to simple string array
    let skillsArray = [];
    if (Array.isArray(job?.skills)) {
      skillsArray = job?.skills?.map(skill => {
        if (typeof skill === 'object' && skill?.name) {
          return skill?.name;
        }
        return skill;
      });
    }
    
    // Format the job data for UI consumption
    return {
      ...camelJob,
      company: job?.employer?.company_name || 'Company Name',
      location: `${job?.city || ''}${job?.city && job?.state ? ', ' : ''}${job?.state || ''}`,
      companyLogo: `https://ui-avatars.com/api/?name=${encodeURIComponent(job?.employer?.company_name || 'C')}&background=random`,
      companyLogoAlt: `${job?.employer?.company_name || 'Company'} logo`,
      title: job?.job_title,
      experience: job?.experience_level,
      employmentTypeFormatted: job?.employment_type?.replace('-', ' ')?.replace(/\b\w/g, l => l?.toUpperCase()),
      workModeFormatted: job?.work_mode?.charAt(0)?.toUpperCase() + job?.work_mode?.slice(1),
      tags: skillsArray,
      postedTime: job?.posted_date ? formatPostedTime(job?.posted_date) : 'Recently'
    };
  },

  // Create new job
  async createJob(jobData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Convert camelCase to snake_case and add employer_id
    const snakeCaseData = toSnakeCase({
      ...jobData,
      employerId: user?.id,
      postedDate: new Date()?.toISOString(),
      status: 'active',
      applicationsCount: 0,
      viewsCount: 0
    });

    const { data, error } = await supabase?.from('jobs')?.insert(snakeCaseData)?.select()?.single();

    if (error) throw error;
    
    return toCamelCase(data);
  },

  // Update job
  async updateJob(jobId, updates) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Convert camelCase to snake_case
    const snakeCaseUpdates = toSnakeCase(updates);

    const { data, error } = await // Ensure user owns the job
    supabase?.from('jobs')?.update(snakeCaseUpdates)?.eq('id', jobId)?.eq('employer_id', user?.id)?.select()?.single();

    if (error) throw error;
    
    return toCamelCase(data);
  },

  // Delete job
  async deleteJob(jobId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('jobs')?.delete()?.eq('id', jobId)?.eq('employer_id', user?.id); // Ensure user owns the job

    if (error) throw error;
    
    return { success: true };
  },

  // Get job statistics for employer
  async getJobStats() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('jobs')?.select('status, applications_count')?.eq('employer_id', user?.id);

    if (error) throw error;

    // Calculate statistics
    const stats = {
      totalJobs: data?.length || 0,
      activeJobs: data?.filter((j) => j?.status === 'active')?.length || 0,
      totalApplications: data?.reduce((sum, j) => sum + (j?.applications_count || 0), 0) || 0,
      draftJobs: data?.filter((j) => j?.status === 'draft')?.length || 0
    };

    return stats;
  },

  // Increment views count
  async incrementViews(jobId) {
    const { error } = await supabase?.rpc('increment_job_views', {
      job_id: jobId
    });

    if (error) {
      // If RPC function doesn't exist, use direct update
      const { data: currentJob } = await supabase?.from('jobs')?.select('views_count')?.eq('id', jobId)?.single();
      const newCount = (currentJob?.views_count || 0) + 1;
      const { error: updateError } = await supabase?.from('jobs')?.update({ views_count: newCount })?.eq('id', jobId);
      
      if (updateError) throw updateError;
    }
  },

  // Get public jobs (for job seekers)
  async getPublicJobs(filters = {}) {
    let query = supabase
      ?.from('jobs')
      ?.select(`
        *,
        employer:user_profiles!employer_id(company_name, location)
      `)
      ?.eq('status', 'active');

    // Apply filters
    if (filters?.employmentType) {
      query = query?.eq('employment_type', filters?.employmentType);
    }
    if (filters?.workMode) {
      query = query?.eq('work_mode', filters?.workMode);
    }
    if (filters?.experienceLevel) {
      query = query?.eq('experience_level', filters?.experienceLevel);
    }
    if (filters?.city) {
      query = query?.ilike('city', `%${filters?.city}%`);
    }

    query = query?.order('posted_date', { ascending: false });

    const { data, error } = await query;

    if (error) throw error;
    
    // Convert and transform data for UI
    return data?.map((job) => {
      const camelJob = toCamelCase(job);
      
      // Handle skills - convert array of objects or strings to simple string array
      let skillsArray = [];
      if (Array.isArray(job?.skills)) {
        skillsArray = job?.skills?.map(skill => {
          // If skill is an object with 'name' property, extract the name
          if (typeof skill === 'object' && skill?.name) {
            return skill?.name;
          }
          // If skill is already a string, use it as-is
          return skill;
        });
      }
      
      return {
        ...camelJob,
        company: job?.employer?.company_name || 'Company Name',
        location: `${job?.city || ''}${job?.city && job?.state ? ', ' : ''}${job?.state || ''}`,
        companyLogo: `https://ui-avatars.com/api/?name=${encodeURIComponent(job?.employer?.company_name || 'C')}&background=random`,
        companyLogoAlt: `${job?.employer?.company_name || 'Company'} logo`,
        title: job?.job_title,
        experience: job?.experience_level,
        salaryMin: job?.salary_min,
        salaryMax: job?.salary_max,
        tags: skillsArray,
        isRemote: job?.work_mode === 'remote',
        isUrgent: job?.urgent_hiring || false,
        postedTime: job?.posted_date ? formatPostedTime(job?.posted_date) : 'Recently',
        isSaved: false // This should come from saved_jobs table later
      };
    }) || [];
  }
};

export default jobService;